import React from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FormControl, MenuItem, Select, SxProps, Theme } from "@mui/material";

/**
 * Consistent Select component with placeholder support and theme-based styling.
 *
 * @example
 * <StyledSelect
 *   value={value}
 *   onChange={(newValue) => setValue(newValue)}
 *   options={[
 *     { value: "option1", label: "Option 1" },
 *     { value: "option2", label: "Option 2" },
 *   ]}
 *   placeholder="Select an option"
 *   data-testid="my-select"
 * />
 *
 * @param value - Current selected value
 * @param onChange - Callback when value changes (receives string value)
 * @param options - Array of {value, label} objects
 * @param placeholder - Optional placeholder text for empty state
 * @param data-testid - Optional test identifier
 */

interface StyledSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  disabled?: boolean;
  "data-testid"?: string;
  sx?: SxProps<Theme>;
}

export const StyledSelect: React.FC<StyledSelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  "data-testid": dataTestId,
  sx,
}) => (
  <FormControl size="small" variant="outlined" fullWidth sx={sx} disabled={disabled}>
    <Select
      value={value}
      onChange={e => onChange(e.target.value)}
      IconComponent={ExpandMoreIcon}
      data-testid={dataTestId}
      displayEmpty
      sx={{
        height: 41,
        backgroundColor: "background.input",
        borderRadius: "7px",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "divider",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "divider",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "primary.main",
        },
        "& .MuiSelect-select": {
          fontSize: 14,
          fontWeight: 500,
          color: value ? "text.primary" : "text.secondary",
          opacity: value ? 1 : 0.7,
        },
        "& .MuiSelect-icon": {
          color: "grey.500",
        },
      }}
    >
      {placeholder && (
        <MenuItem value="" disabled>
          {placeholder}
        </MenuItem>
      )}
      {options.map(option => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);
